import type { FeatureFlags, ServerConfiguration } from '@/domain/configuration';
import { isButtonLauncherForced } from '@/domain/launcherButton';
import { isRenderedLauncherType, LAUNCHER_TYPES } from '@/domain/launcherTypes';
import { isLegacyUIEnabled } from '@/domain/legacyUiConfig';
import { PROMPTS_SOURCES } from '@/domain/messageEnums';
import type { V2Launcher } from '@/launchers/types';
import { findChatBarLauncher } from '@/lib/chatBar/findChatBarLauncher';
import { CHAT_BAR_DISPLAY, type ChatBarDisplay } from '@/lib/displayRules/evaluateUrlDisplayRules';
import { isChatBarVisible } from '@/lib/displayRules/isChatBarVisible';
import { isLauncherVisible } from '@/lib/displayRules/isLauncherVisible';
import { asRecord } from '@/lib/utils/isRecord';

/**
 * The launchers this page's config and display rules admit, and that ask for generated
 * prompts — a prediction, not an observation. This runs before the launcher tier mounts
 * anything, and the render path gates further on a payload validator and on finding the
 * anchor in the DOM, neither of which is knowable here.
 *
 * Read per send rather than held: display rules are evaluated against the URL, and one
 * tracker outlives a navigation. A `displayDelay` decides when a launcher appears, not
 * whether, so it still counts.
 */
export function visibleLauncherIds(
  serverConfig: ServerConfiguration,
  featureFlags: FeatureFlags | undefined,
): string[] {
  const launchers = serverConfig.v2?.launchers;
  if (!launchers?.length) return [];

  const ids: string[] = [];

  // The bar is the bootstrap entry point, not part of the launcher tier, so
  // enableChatLaunchers does not gate it. A forced button does: Legacy UI, or a
  // `v2.display` of BUTTON from the configuration override. The test is whether the bar
  // can ever render rather than what is on screen: such a page never renders one, so its
  // prompts would have nowhere to land. A dismissal shows the same corner bubble and is
  // not the same thing — it expires after a day and the bar returns. A custom button
  // needs Legacy UI too, so this covers it.
  const chatBar = findChatBarLauncher(launchers);
  const drawsChatBar = !isButtonLauncherForced(serverConfig);
  if (chatBar && asksForPrompts(chatBar) && drawsChatBar && pageDrawsBar(serverConfig)) {
    ids.push(chatBar.id);
  }

  ids.push(
    ...renderedLaunchers(serverConfig, featureFlags)
      .filter(asksForPrompts)
      .map((launcher) => launcher.id),
  );

  return ids;
}

/**
 * Whether this page shows an entry point at all: the bar, the corner bubble (all Legacy UI
 * draws), or a button launcher. Display rules are the one lever a merchant has to say the
 * widget should not operate on a page, so a page where they hide every entry point sends
 * no page view. A page with an entry point sends one even when nothing on it asks for
 * prompts, because Beacon categorizes every page it hears about.
 *
 * Not the same as having launcher ids: the corner bubble and static launchers are entry
 * points that name no launcher, and an org with no CHAT_BAR entry still draws a bar from
 * `v2.behavior.displayRules`, which `isChatBarVisible` falls back to.
 */
export function pageShowsEntryPoint(
  serverConfig: ServerConfiguration,
  featureFlags: FeatureFlags | undefined,
): boolean {
  if (chatBarDisplay(serverConfig) !== CHAT_BAR_DISPLAY.HIDDEN) return true;
  return renderedLaunchers(serverConfig, featureFlags).length > 0;
}

// Read from the raw payload: the button's validator lives in the launcher tier, which has not
// loaded yet. Anything but AI is STATIC, as both validators read it.
function asksForPrompts(launcher: V2Launcher): boolean {
  const payload = asRecord(launcher.payload);
  const source = launcher.type === LAUNCHER_TYPES.CHAT_BAR ? payload?.placeholdersSource : payload?.promptsSource;
  return source === PROMPTS_SOURCES.AI;
}

/**
 * Only the bar itself: a page whose rules ask for the button draws the same corner
 * bubble Legacy UI does, and for the same reason gives a prompt nowhere to land.
 */
function pageDrawsBar(serverConfig: ServerConfiguration): boolean {
  return chatBarDisplay(serverConfig) === CHAT_BAR_DISPLAY.BAR;
}

function chatBarDisplay(serverConfig: ServerConfiguration): ChatBarDisplay {
  return isChatBarVisible({
    defaultDisplay: serverConfig.v2?.display,
    displayRules: serverConfig.v2?.behavior?.displayRules,
    launchers: serverConfig.v2?.launchers,
  }).display;
}

/** Fail closed exactly as startLaunchers does: an absent flag or Legacy UI draws none of them. */
function renderedLaunchers(serverConfig: ServerConfiguration, featureFlags: FeatureFlags | undefined): V2Launcher[] {
  if (!featureFlags?.enableChatLaunchers || isLegacyUIEnabled(serverConfig)) return [];
  return (serverConfig.v2?.launchers ?? []).filter(
    (launcher) => isRenderedLauncherType(launcher.type) && isLauncherVisible(launcher),
  );
}
